import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon, UserGroupIcon, ClockIcon } from '@heroicons/react/24/outline';
import { Button } from '../../ui';

interface SponsorReminderModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddSponsor: () => void;
  onRemindLater: () => void;
}

export function SponsorReminderModal({
  isOpen,
  onClose,
  onAddSponsor,
  onRemindLater,
}: SponsorReminderModalProps) {
  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-4 top-1/2 -translate-y-1/2 z-50 mx-auto max-w-md"
          >
            <div className="bg-white dark:bg-surface-800 rounded-2xl shadow-xl overflow-hidden">
              {/* Header */}
              <div className="relative bg-gradient-to-br from-primary-500 to-accent-500 px-6 py-8 text-center">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 p-1 rounded-full bg-white/20 hover:bg-white/30 transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 text-white" />
                </button>

                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 mb-4">
                  <UserGroupIcon className="w-8 h-8 text-white" />
                </div>

                <h2 className="text-xl font-display font-semibold text-white">
                  Connect with Your Sponsor
                </h2>
                <p className="text-white/80 text-sm mt-2">
                  Having a sponsor increases your chances of long-term recovery success
                </p>
              </div>

              {/* Content */}
              <div className="px-6 py-6">
                <p className="text-surface-600 dark:text-surface-400 text-center mb-6">
                  During setup, you mentioned you have a sponsor but didn't add their contact info.
                  Would you like to add them now?
                </p>

                <div className="space-y-3">
                  <Button onClick={onAddSponsor} size="lg" className="w-full">
                    Add Sponsor Now
                  </Button>

                  <button
                    onClick={onRemindLater}
                    className="w-full flex items-center justify-center gap-2 py-3 px-4 text-sm text-surface-600 dark:text-surface-400 hover:text-surface-800 dark:hover:text-surface-200 hover:bg-surface-100 dark:hover:bg-surface-700 rounded-xl transition-colors"
                  >
                    <ClockIcon className="w-4 h-4" />
                    Remind me in a few days
                  </button>
                </div>
              </div>

              {/* Footer tip */}
              <div className="px-6 pb-6">
                <p className="text-xs text-surface-500 dark:text-surface-500 text-center">
                  You can always add sponsor info later from your profile settings
                </p>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
